import axios from 'axios';

// No explicit baseURL; proxy will handle it, with fallback to hosted URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://turemedicine-backend-1.onrender.com',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 30000, // Set timeout to 30 seconds for all requests
});

api.interceptors.request.use((config) => {
  const patientToken = localStorage.getItem('patientAccessToken');
  const doctorToken = localStorage.getItem('doctorAccessToken');
  const adminToken = localStorage.getItem('adminAccessToken');
  const url = config.url || '';

  console.log(`Request URL: /api${url}`); // Proxy prepends /api typically; adjust as needed
  console.log(`Available tokens:`, {
    patient: patientToken?.substring(0, 10) + '...',
    doctor: doctorToken?.substring(0, 10) + '...',
    admin: adminToken?.substring(0, 10) + '...',
  });

  const skipAuthEndpoints = [
    '/auth/doctor/login',
    '/auth/patient/login',
    '/admin/login',
    '/auth/refresh',
    '/admin/refresh',
    '/auth/patient/forgot-password',
    '/auth/doctor/forgot-password',
    '/auth/patient/reset-password',
    '/auth/doctor/reset-password',
  ];
  if (skipAuthEndpoints.some(endpoint => url.includes(endpoint)) || config.headers['No-Auth']) {
    console.log(`Skipping token attachment for ${url}`);
    return config;
  }

  if (config.headers.Authorization) {
    const providedToken = config.headers.Authorization.startsWith('Bearer ')
      ? config.headers.Authorization
      : `Bearer ${config.headers.Authorization}`;
    console.log(`Using provided token for ${url}: ${providedToken.substring(0, 10)}...`);
    config.headers.Authorization = providedToken;
    return config;
  }

  let token;
  if (
    url.includes('/patients') ||
    url.includes('/appointments/book') ||
    (url.includes('/appointments/patient/') && !url.includes('/history') && !url.includes('/records')) ||
    url.includes('/notifications/patient') ||
    url.includes('/feedback')
  ) {
    token = patientToken;
    console.log(`Selected patient token for ${url}: ${token?.substring(0, 10) || 'undefined'}...`);
  } else if (
    url.includes('/doctors') ||
    url.includes('/appointments/doctor') ||
    (url.includes('/appointments/patient') && (url.includes('/history') || url.includes('/records'))) ||
    url.includes('/notifications/doctor') ||
    url.includes('/prescriptions') ||
    url.match(/^\/appointments\/[a-f\d]{24}$/)
  ) {
    token = doctorToken;
    console.log(`Selected doctor token for ${url}: ${token?.substring(0, 10) || 'undefined'}...`);
  } else if (url.includes('/admin') || url === '/notifications') {
    token = adminToken;
    console.log(`Selected admin token for ${url}: ${token?.substring(0, 10) || 'undefined'}...`);
  } else {
    token = patientToken || doctorToken || adminToken;
    console.warn(`Ambiguous route ${url} - Using fallback token: ${token?.substring(0, 10) || 'undefined'}...`);
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log(`Attached token for ${url}: Bearer ${token.substring(0, 10)}...`);
  } else {
    console.log(`No token available for ${url} - Proceeding without auth`);
  }

  return config;
}, (error) => {
  console.error('Request interceptor error:', error.message);
  return Promise.reject(error);
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest.url || '';

    console.error(`API error for ${url}:`, {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      config: error.config,
    });

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      ![
        '/auth/doctor/login',
        '/auth/patient/login',
        '/admin/login',
        '/auth/refresh',
        '/admin/refresh',
        '/auth/patient/forgot-password',
        '/auth/doctor/forgot-password',
        '/auth/patient/reset-password',
        '/auth/doctor/reset-password',
      ].some(endpoint => url.includes(endpoint))
    ) {
      originalRequest._retry = true;

      const routeMappings = [
        {
          condition: url.includes('/patients') ||
            url.includes('/appointments/book') ||
            (url.includes('/appointments/patient/') && !url.includes('/history') && !url.includes('/records')) ||
            url.includes('/notifications/patient') ||
            url.includes('/feedback'),
          role: 'patient',
          accessTokenKey: 'patientAccessToken',
          refreshTokenKey: 'patientRefreshToken',
          idKey: 'patientId',
          refreshEndpoint: '/auth/refresh',
          redirectPath: '/login?type=patient',
        },
        {
          condition: url.includes('/doctors') ||
            url.includes('/appointments/doctor') ||
            (url.includes('/appointments/patient') && (url.includes('/history') || url.includes('/records'))) ||
            url.includes('/notifications/doctor') ||
            url.includes('/prescriptions') ||
            url.match(/^\/appointments\/[a-f\d]{24}$/),
          role: 'doctor',
          accessTokenKey: 'doctorAccessToken',
          refreshTokenKey: 'doctorRefreshToken',
          idKey: 'doctorId',
          refreshEndpoint: '/auth/refresh',
        },
        {
          condition: url.includes('/admin') || url === '/notifications',
          role: 'admin',
          accessTokenKey: 'adminAccessToken',
          refreshTokenKey: 'adminRefreshToken',
          idKey: 'adminId',
          refreshEndpoint: '/admin/refresh',
        },
      ];

      const route = routeMappings.find(mapping => mapping.condition) || routeMappings[0];
      const { role, accessTokenKey, refreshTokenKey, idKey, refreshEndpoint } = route;
      const refreshToken = localStorage.getItem(refreshTokenKey);

      if (refreshToken) {
        try {
          console.log(`Attempting token refresh for ${role} with token: ${refreshToken.substring(0, 10)}...`);
          const refreshResponse = await axios.post(refreshEndpoint, { refreshToken }); // Relative URL via proxy
          const { accessToken } = refreshResponse.data;
          localStorage.setItem(accessTokenKey, accessToken);
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          console.log(`Token refreshed for ${role}: ${accessToken.substring(0, 10)}...`);
          return api(originalRequest);
        } catch (refreshError) {
          console.error(`Token refresh failed for ${role}:`, refreshError.response?.data || refreshError.message);
          localStorage.removeItem(accessTokenKey);
          localStorage.removeItem(refreshTokenKey);
          localStorage.removeItem(idKey);
          if (role === 'admin') {
            localStorage.removeItem('adminRole');
            window.location.href = '/admin';
          }
          return Promise.reject(error);
        }
      } else {
        console.warn(`No refresh token found for ${role} role`);
        if (role === 'admin') {
          window.location.href = '/admin';
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export default api;